// src/components/AuthPage.jsx
// Login + Register card. Invite-token aware.

import React, { useState as useState_AU } from "react";
import * as PPIcons from "./_icons";
import { Button, Field } from "./_primitives";
import PinchIcon from "./PinchIcon";

function AuthPage({ initialMode = "login", inviteToken = "", onLogin, onRegister, onBack }) {
  const [mode, setMode] = useState_AU(initialMode);
  const [email, setEmail] = useState_AU("");
  const [password, setPassword] = useState_AU("");
  const [displayName, setDisplayName] = useState_AU("");
  const [orgMode, setOrgMode] = useState_AU(inviteToken ? "join" : "create");
  const [orgName, setOrgName] = useState_AU("");
  const [agreed, setAgreed] = useState_AU(false);
  const [error, setError] = useState_AU("");
  const [busy, setBusy] = useState_AU(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") {
        await onLogin?.(email, password);
      } else {
        if (!agreed) throw new Error("Please agree to the Terms.");
        await onRegister?.(email, password, displayName, orgMode, orgName, inviteToken);
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "grid", placeItems: "center",
      background: "var(--bg)", padding: 24,
    }}>
      <div style={{
        width: "100%", maxWidth: 420, background: "var(--bg-elevated)",
        border: "1px solid var(--border)", borderRadius: "var(--radius-lg)",
        padding: 32, boxShadow: "var(--shadow-lg)",
      }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: "var(--accent)",
            color: "var(--accent-fg)", display: "grid", placeItems: "center",
            marginBottom: 14, boxShadow: "var(--shadow)",
          }}>
            <PinchIcon size={28} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 4 }}>
            {mode === "login" ? "Sign in to Pinch Point" : "Set up your team's friction log"}
          </p>
        </div>

        {/* Mode switcher */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4,
          background: "var(--surface-2)", padding: 4,
          borderRadius: "var(--radius-sm)", marginBottom: 18,
        }}>
          {["login", "register"].map(m => (
            <button key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                padding: 8, border: 0, cursor: "pointer",
                borderRadius: 4, fontWeight: 600, fontSize: 13,
                background: mode === m ? "var(--surface)" : "transparent",
                color: mode === m ? "var(--text-strong)" : "var(--text-dim)",
                boxShadow: mode === m ? "var(--shadow-xs)" : "none",
              }}>
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {error && (
            <div style={{
              background: "var(--pp-crit-bg)", color: "var(--pp-crit)",
              border: "1px solid var(--pp-crit-bd)",
              borderRadius: "var(--radius-sm)", padding: "8px 12px",
              fontSize: 12, fontWeight: 600,
            }}>{error}</div>
          )}

          {mode === "register" && (
            <Field label="Full name">
              <input className="pp-input" placeholder="Maya Bell"
                value={displayName} onChange={e => setDisplayName(e.target.value)} required />
            </Field>
          )}

          <Field label="Email">
            <input className="pp-input" type="email" placeholder="you@company.com"
              value={email} onChange={e => setEmail(e.target.value)} required />
          </Field>

          <Field label="Password">
            <input className="pp-input" type="password" placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
          </Field>

          {mode === "register" && !inviteToken && (
            <div style={{
              background: "var(--surface-2)", padding: 12,
              borderRadius: "var(--radius-sm)",
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              <div style={{ display: "flex", gap: 4 }}>
                {[
                  { v: "create", l: "Create new org" },
                  { v: "join",   l: "Join existing" },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => setOrgMode(o.v)}
                    style={{
                      flex: 1, padding: "8px 10px", borderRadius: 6,
                      border: "1px solid",
                      borderColor: orgMode === o.v ? "var(--accent)" : "var(--border)",
                      background: orgMode === o.v ? "var(--accent-soft)" : "var(--surface)",
                      color: orgMode === o.v ? "var(--accent)" : "var(--text-dim)",
                      fontWeight: 700, fontSize: 12, cursor: "pointer",
                    }}>{o.l}</button>
                ))}
              </div>
              {orgMode === "create" && (
                <Field label="Organization name">
                  <input className="pp-input" placeholder="Skybridge Aviation"
                    value={orgName} onChange={e => setOrgName(e.target.value)} required />
                </Field>
              )}
            </div>
          )}

          {mode === "register" && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--text-dim)", cursor: "pointer" }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
              <span>
                I agree to the <a href="/terms" style={{ color: "var(--link)" }}>Terms</a> and {" "}
                <a href="/privacy" style={{ color: "var(--link)" }}>Privacy Policy</a>.
              </span>
            </label>
          )}

          <Button variant="primary" size="lg" type="submit" disabled={busy}>
            {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        {onBack && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <button onClick={onBack} style={{
              background: "transparent", border: 0, color: "var(--text-dim)",
              fontSize: 12, cursor: "pointer", textDecoration: "underline",
            }}>← Back to home</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthPage;
